import { ESdChangeReason, IActionDefinitionDto, IDatasourceDto } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import * as React from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaEditorHeader from "../../ui-kit/kuika-editor-header/kuika-editor-header";
import { KuikaEditorTitle } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../ui-kit/typography/SmallMediumText/SmallMediumText";
import { requestDatasourceReveal } from "./datasource-reveal";
import { SapActionSwitcherChip } from "./editor-switcher/sap-action-switcher-chip";
import { ISapFileContentOverride, SapFileContentField, SapSignatureGrid } from "./sap-signature-grid";
import "./sap-action-ui.scss";
import "./editor-bar-surface.scss";

export interface ICreateSapActionProps {
  actionDatasource: IDatasourceDto;
  actionToEdit?: IActionDefinitionDto;
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

export interface ICreateSapActionState {
  /** Every fetch this panel makes, not just the commit — the metadata lookups share it. */
  isLoading: boolean;
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  functionGroups: { label: string; value: string }[];
  functions: { label: string; value: string }[];
  functionSignatureJson?: any;
  prefilledGroupSearched?: boolean;
  isSearchingGroups?: boolean;
  isSearchingFunctions?: boolean;
  fileContentOverrides?: Record<string, ISapFileContentOverride>;
}

/** What the new action is called until a function names it. */
const NEW_SAP_ACTION_TITLE: string = "New SAP Action";

class CreateSapAction extends PureSdComponent<ICreateSapActionProps & IWithSDProps, ICreateSapActionState> {
  formRef = React.createRef<FormInstance>();

  private fgSearchTimer: any;

  private fnSearchTimer: any;

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ICreateSapActionProps & IWithSDProps) {
    super(props);
    this.state = {
      isLoading: false,
      saveStatus: "idle",
      functionGroups: [],
      functions: [],
      prefilledGroupSearched: false
    };
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    if (this.fgSearchTimer) clearTimeout(this.fgSearchTimer);
    if (this.fnSearchTimer) clearTimeout(this.fnSearchTimer);
  }

  private normalizeFunctionName = (name?: string): string | undefined => name;

  closePanel = () => {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
  };

  /**
   * The chip names the action as it will be saved: a SAP action is named after the function it
   * calls, so there is no pencil — and until one is picked it is simply the new one. It re-reads
   * the field on every store change, `setFieldsValue` included, so a cleared group clears it too.
   */
  private renderTitle = (): React.ReactNode => (
    <Form.Item noStyle shouldUpdate>
      {(): React.ReactNode => {
        const functionName: string | undefined = this.formRef.current?.getFieldValue("sapFunction");
        return (
          <KuikaEditorTitle
            entityLabel="SAP action"
            canRename={false}
            identity={
              <SapActionSwitcherChip
                sd={this.props.sd}
                setNextCallbackFunction={this.props.setNextCallbackFunction}
                datasource={this.props.actionDatasource}
                currentActionId={undefined}
                currentName={functionName || NEW_SAP_ACTION_TITLE}
                onManageAll={this.closePanel}
              />
            }
          />
        );
      }}
    </Form.Item>
  );

  render() {
    const signature = this.getNormalizedSignature();
    return (
      <Form ref={this.formRef} layout="vertical" onValuesChange={this.handleFormValuesChange}>
        <div className="kuika__data-diagram__create-con kuika__data-diagram__create-con--bar">
          <KuikaEditorHeader
            title={NEW_SAP_ACTION_TITLE}
            backLabel="Back to Datasources"
            renderTitle={this.renderTitle}
            submitLabel={this.props.actionToEdit ? "UPDATE" : "CREATE"}
            onSubmit={this.handleCreateSapAction}
            status={this.state.saveStatus}
            onCancel={this.closePanel}
            cancelTestId="sapActionBackButton"
            submitTestId="sapActionSubmitButton"
          />

          <div className="kuika__data-diagram__create-con__container sap-action-container">
            <KuikaSpinnerSmall spinning={this.state.isLoading} size="small">
              <div className="kuika__data-diagram__create-con__container__section sap-action-section">
                <div className="sap-row">
                  <div className="kuika__data-diagram__create-con__container__field-item sap-field-item sap-col">
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <KuikaSmallMediumText>Function Group</KuikaSmallMediumText>
                      <KuikaTooltip
                        content={
                          "Search only within this group. The list auto-loads when opened. Changing the group clears the selected function. Type at least 3 characters to search."
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6
                          }}
                        >
                          <KuikaSvg name="info-circle" width={16} height={16} pointer />
                        </div>
                      </KuikaTooltip>
                    </div>
                    <Form.Item
                      name="sapFunctionGroup"
                      rules={[
                        {
                          required: true,
                          message: "Please select function group"
                        }
                      ]}
                    >
                      <KuikaSelectbox
                        className="sap-select"
                        placeholder="Select function group"
                        data-testid="selectFunctionGroupDropdown"
                        searchable
                        allowClear
                        onClear={() => {
                          if (this.fgSearchTimer) clearTimeout(this.fgSearchTimer);
                          if (this.fnSearchTimer) clearTimeout(this.fnSearchTimer);
                          this.formRef.current?.setFieldsValue({
                            sapFunctionGroup: null,
                            sapFunction: null
                          });
                          this.setState({
                            isSearchingGroups: false,
                            isSearchingFunctions: false,
                            isLoading: false,
                            functionGroups: [],
                            functions: [],
                            functionSignatureJson: undefined
                          });
                        }}
                        suffixIcon={
                          this.state.isSearchingGroups ? (
                            <span className="sap-inline-loading" aria-label="Loading" />
                          ) : undefined
                        }
                        onSearch={async (input: string) => {
                          const dsId = this.props.actionDatasource?.id;
                          if (!dsId) return;
                          if (!input || input.length < 3) {
                            if (this.fgSearchTimer) clearTimeout(this.fgSearchTimer);
                            this.setState({ functionGroups: [] });
                            return;
                          }
                          if (this.fgSearchTimer) clearTimeout(this.fgSearchTimer);
                          this.setState({ isSearchingGroups: true });
                          this.fgSearchTimer = setTimeout(async () => {
                            try {
                              const res = await DatasourceService.GetSapFunctionGroups(dsId, input);
                              const functionGroups = (res?.data || []).map((x: any) => ({
                                label: x.groupName,
                                value: x.groupName
                              }));
                              this.setState({ functionGroups });
                            } catch {}
                            this.setState({ isSearchingGroups: false });
                          }, 300);
                        }}
                        options={this.state.functionGroups}
                      />
                    </Form.Item>
                  </div>

                  <div className="kuika__data-diagram__create-con__container__field-item sap-col sap-field-item">
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <KuikaSmallMediumText>Function</KuikaSmallMediumText>
                      <KuikaTooltip
                        content={
                          "With a selected group, results are limited to that group and the list opens preloaded. Without a group, search all functions; type at least 3 characters."
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6
                          }}
                        >
                          <KuikaSvg name="info-circle" width={16} height={16} pointer />
                        </div>
                      </KuikaTooltip>
                    </div>
                    <Form.Item name="sapFunction" rules={[{ required: true, message: "Please select function" }]}>
                      <KuikaSelectbox
                        className="sap-select"
                        placeholder="Select function"
                        data-testid="selectFunctionDropdown"
                        searchable
                        allowClear
                        onClear={() => {
                          this.formRef.current?.setFieldsValue({
                            sapFunction: null
                          });
                          this.setState({ functionSignatureJson: undefined });
                        }}
                        suffixIcon={
                          this.state.isSearchingFunctions ? (
                            <span className="sap-inline-loading" aria-label="Loading" />
                          ) : undefined
                        }
                        onSearch={async (input: string) => {
                          const dsId = this.props.actionDatasource?.id;
                          if (!dsId) return;
                          const hasGroup = !!this.formRef.current?.getFieldValue("sapFunctionGroup");
                          if (!hasGroup && (!input || input.length < 3)) {
                            if (this.fnSearchTimer) clearTimeout(this.fnSearchTimer);
                            this.setState({ functions: [] });
                            return;
                          }
                          if (this.fnSearchTimer) clearTimeout(this.fnSearchTimer);
                          this.setState({ isSearchingFunctions: true });
                          this.fnSearchTimer = setTimeout(async () => {
                            try {
                              const groupVal = this.formRef.current?.getFieldValue("sapFunctionGroup");
                              await this.loadFunctions(groupVal, input || undefined);
                            } catch {}
                            this.setState({ isSearchingFunctions: false });
                          }, 300);
                        }}
                        options={this.state.functions}
                      />
                    </Form.Item>
                  </div>
                </div>

                {signature && (
                  <>
                    <div className="sap-row">
                      <div className="kuika__data-diagram__create-con__container__field-item sap-field-item sap-col">
                        <KuikaSmallMediumText>Inputs</KuikaSmallMediumText>
                        <SapSignatureGrid
                          label="Inputs"
                          params={signature.Inputs}
                          emptyText="No inputs"
                          testIdPrefix="sapInputs"
                          fileContentOverrides={this.state.fileContentOverrides}
                          onFileContentChange={this.handleFileContentChange}
                        />
                      </div>

                      <div className="kuika__data-diagram__create-con__container__field-item sap-field-item sap-col">
                        <KuikaSmallMediumText>Outputs</KuikaSmallMediumText>
                        <SapSignatureGrid
                          label="Outputs"
                          params={signature.Outputs}
                          emptyText="No outputs"
                          testIdPrefix="sapOutputs"
                          fileContentOverrides={this.state.fileContentOverrides}
                          onFileContentChange={this.handleFileContentChange}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </KuikaSpinnerSmall>
          </div>
        </div>
      </Form>
    );
  }

  componentDidMount() {
    // No initial fetches; fetches should start only after user types ≥ 3 chars
    // If editing, prefill from existing action's sapActionInfo
    if (this.props.actionToEdit?.sapActionInfo) {
      try {
        const json =
          typeof this.props.actionToEdit.sapActionInfo === "string"
            ? JSON.parse(this.props.actionToEdit.sapActionInfo)
            : this.props.actionToEdit.sapActionInfo;
        const funcName = json?.Function || this.props.actionToEdit.name;
        if (funcName) {
          this.formRef.current?.setFieldsValue({ sapFunction: funcName });
          this.setState({ functionSignatureJson: json });
        }
        // Mevcut ParameterConfig varsa filename/MIME override'larını state'e yükle.
        const cfg = json?.ParameterConfig;
        if (cfg && typeof cfg === "object") {
          const overrides: { [k: string]: ISapFileContentOverride } = {};
          for (const name of Object.keys(cfg)) {
            const entry = cfg[name];
            if (!entry || typeof entry !== "object") continue;
            overrides[name] = {
              fileNameTemplate: typeof entry.fileNameTemplate === "string" ? entry.fileNameTemplate : undefined,
              mimeType: typeof entry.mimeType === "string" ? entry.mimeType : undefined
            };
          }
          if (Object.keys(overrides).length > 0) {
            this.setState({ fileContentOverrides: overrides });
          }
        }
        // Try to infer group from existing list when loaded later
      } catch {}
    }
  }

  componentDidUpdate(prevProps: ICreateSapActionProps & IWithSDProps) {
    // Do not auto-load function groups when datasource changes; wait for user input
    // When groups are loaded and editing, attempt to auto-select group containing the function
    if (
      !this.state.prefilledGroupSearched &&
      this.props.actionToEdit?.sapActionInfo &&
      this.state.functionGroups.length > 0 &&
      this.formRef.current?.getFieldValue("sapFunction") &&
      this.props.actionDatasource?.id
    ) {
      this.setState({ prefilledGroupSearched: true });
      const func = this.formRef.current?.getFieldValue("sapFunction");
      // Best effort: iterate groups and find one that contains function
      // We will call GetSapFunctions per group until we find a match (lightweight lists)
      (async () => {
        for (const g of this.state.functionGroups) {
          try {
            const res = await DatasourceService.GetSapFunctions(this.props.actionDatasource!.id, g.value);
            const functions = (res?.data || []).map((x: any) => x.functionName || x.funcName || x.name);
            if (functions.includes(func)) {
              this.formRef.current?.setFieldsValue({
                sapFunctionGroup: g.value
              });
              this.setState({
                functions: functions.map((f: string) => ({
                  label: f,
                  value: f
                }))
              });
              break;
            }
          } catch {}
        }
      })();
    }
  }

  private async loadFunctionGroups() {
    const dsId = this.props.actionDatasource?.id;
    if (!dsId) return;
    this.setState({ isLoading: true });
    try {
      const res = await DatasourceService.GetSapFunctionGroups(dsId);
      const functionGroups = (res?.data || []).map((x: any) => ({
        label: x.groupName,
        value: x.groupName
      }));
      this.setState({ functionGroups });
    } catch (e) {
    } finally {
      this.setState({ isLoading: false });
    }
  }

  private async loadFunctions(functionGroup?: string, searchKey?: string) {
    const dsId = this.props.actionDatasource?.id;
    if (!dsId) return;
    this.setState({ isLoading: true });
    try {
      const res = await DatasourceService.GetSapFunctions(dsId, functionGroup, searchKey);
      const functions = (res?.data || []).map((x: any) => ({
        label: x.functionName || x.funcName || x.name,
        value: x.functionName || x.funcName || x.name,
        groupName: x.groupName || x.group || x.GROUPNAME
      }));
      const seenGroups = new Set<string>();
      const functionGroups = functions.reduce((acc: { label: string; value: string }[], f: any) => {
        const g = f.groupName;
        if (g && !seenGroups.has(g)) {
          seenGroups.add(g);
          acc.push({ label: g, value: g });
        }
        return acc;
      }, []);

      this.setState({ functions, functionGroups });
    } catch (e) {
    } finally {
      this.setState({ isLoading: false });
    }
  }

  private handleFormValuesChange = async (changedValues: any, allValues: any) => {
    if (Object.prototype.hasOwnProperty.call(changedValues, "sapFunctionGroup")) {
      const groupVal = changedValues.sapFunctionGroup;
      if (groupVal === undefined || groupVal === null || groupVal === "") {
        if (this.fnSearchTimer) clearTimeout(this.fnSearchTimer);
        this.setState({
          isSearchingFunctions: false,
          isLoading: false,
          functions: [],
          functionSignatureJson: undefined
        });
        this.formRef.current?.setFieldsValue({ sapFunction: null });
        return;
      }
      this.setState({
        isSearchingFunctions: true,
        functions: [],
        functionSignatureJson: undefined
      });
      try {
        await this.loadFunctions(groupVal);
      } catch {}
      this.setState({ isSearchingFunctions: false });
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, "sapFunction")) {
      const funcVal = changedValues.sapFunction;
      const dsId = this.props.actionDatasource?.id;
      if (!dsId || !funcVal) {
        this.setState({ functionSignatureJson: undefined });
        return;
      }
      const matched = (this.state.functions || []).find((f: any) => (f as any)?.value === funcVal);
      const groupFromMatch = (matched as any)?.groupName;
      const currentGroup = this.formRef.current?.getFieldValue("sapFunctionGroup");
      if (groupFromMatch && groupFromMatch !== currentGroup) {
        this.formRef.current?.setFieldsValue({
          sapFunctionGroup: groupFromMatch
        });
      }
      this.setState({ isLoading: true, functionSignatureJson: undefined });
      try {
        const res = await DatasourceService.GetSapFunctionSignature(dsId, funcVal);
        this.setState({ functionSignatureJson: res?.data });
      } catch (e) {
      } finally {
        this.setState({ isLoading: false });
      }
    }
  };

  private getParsedSignature(): any | null {
    const data = this.state.functionSignatureJson;
    if (!data) return null;
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch (err) {
        return null;
      }
    }
    return data;
  }

  private getNormalizedSignature(): any | null {
    const sig = this.getParsedSignature();
    if (!sig) return null;
    return Array.isArray(sig) ? sig[0] : sig;
  }

  /** A file output's name template or MIME type, as typed under its row. */
  handleFileContentChange = (paramName: string, field: SapFileContentField, value: string): void => {
    const next: Record<string, ISapFileContentOverride> = { ...(this.state.fileContentOverrides ?? {}) };
    next[paramName] = { ...(next[paramName] ?? {}), [field]: value };
    this.setState({ fileContentOverrides: next });
  };

  /**
   * SAP signature JSON'una ParameterConfig bloğunu enjekte eder. Her IsFileContent=true
   * output parametresi için backend'in [SapFileContent] attribute'u emit edebilmesi
   * için chunkSize / rowFieldName / sapBinarySubtype'ı toparlar; filename template ve
   * MIME type'ı kullanıcının designer input'larından alır. Default'lar (octet-stream)
   * input boş ise uygulanır.
   */
  private buildSapActionInfoWithParameterConfig(sig: any): any {
    const overrides = this.state.fileContentOverrides || {};
    const outputs: any[] = Array.isArray(sig?.Outputs) ? sig.Outputs : [];
    const parameterConfig: { [k: string]: any } = {};
    for (const p of outputs) {
      if (!p?.IsFileContent) continue;
      const ov = overrides[p.Name] || {};
      parameterConfig[p.Name] = {
        isFileContent: true,
        chunkSize: typeof p.ChunkSize === "number" ? p.ChunkSize : 0,
        sapBinarySubtype: p.SapBinarySubtype || "",
        rowFieldName: typeof p.RowFieldName === "string" ? p.RowFieldName : "",
        fileNameTemplate:
          (ov.fileNameTemplate && ov.fileNameTemplate.trim()) || `${p.Name?.toLowerCase?.() || "sap_file"}.bin`,
        mimeType: (ov.mimeType && ov.mimeType.trim()) || "application/octet-stream"
      };
    }
    const merged = { ...(sig || {}) };
    if (Object.keys(parameterConfig).length > 0) {
      merged.ParameterConfig = parameterConfig;
    }
    return merged;
  }

  private handleCreateSapAction = async (): Promise<void> => {
    const dsId = this.props.actionDatasource?.id;
    if (!dsId) return;
    const actionName = this.formRef.current?.getFieldValue("sapFunction");
    if (!actionName) return;
    // The commit button is live again as soon as the ✓ replaces the spinner, so a second press
    // would re-fire the save behind the closing panel.
    if (!this.saveStatus.canStart) return;
    try {
      this.setState({ isLoading: true });
      const sigObj =
        typeof this.state.functionSignatureJson === "string"
          ? (() => {
              try {
                return JSON.parse(this.state.functionSignatureJson);
              } catch {
                return {};
              }
            })()
          : (this.state.functionSignatureJson ?? {});
      const sigWithConfig = this.buildSapActionInfoWithParameterConfig(sigObj);
      const sapInfoString = JSON.stringify(sigWithConfig);
      const payload: IActionDefinitionDto = {
        id: undefined as any,
        name: actionName,
        kDatasource_Id: dsId,
        kDatasource_Name: this.props.actionDatasource?.name,
        actionTemplate: "",
        statement: "",
        returnTypeModel: undefined as any,
        returnTypeName: "",
        code: "",
        isAsync: false,
        actionType: this.props.actionDatasource?.datasourceType as any,
        returnCardinality: undefined as any,
        statementType: undefined as any,
        actionParameters: [],
        parameters: [],
        actionCategory: undefined as any,
        actionSubCategory: undefined as any,
        appSupportType: undefined as any,
        requiresConnection: false,
        bluetoothInfo: null,
        aiActionInfo: null,
        blocklyActionInfo: null,
        javaScriptActionInfo: null,
        isServerSideAction: false,
        genericSelectData: undefined,
        aiAgentInfo: null,
        sapActionInfo: sapInfoString
      };
      const res = await DatasourceService.SaveAction(payload);
      if (res?.status !== 200) {
        // A resolved response that is not 200: the `finally` reset used to rescue this branch.
        this.saveStatus.fail();
        return;
      }
      await this.props.sd.loadActionDefinitions();
      // The ✓ goes up first and the panel leaves after it — see `SaveStatus`.
      this.saveStatus.succeed((): void => {
        // The list behind the panel lights the row up; the button has already said "Saved".
        requestDatasourceReveal({ name: actionName, parentId: dsId });
        this.closePanel();
      });
    } catch (e) {
      this.saveStatus.fail();
    } finally {
      this.setState({ isLoading: false });
    }
  };

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb)),
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null))
});

const createSapAction = connect(mapStateToProps, mapDispatchToProps)(withSD<ICreateSapActionProps>(CreateSapAction));
export { createSapAction as CreateSapAction };
